import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

/** How long a press must be held before it counts. Below ~400ms it fires while scrolling. */
const LONG_PRESS_MS = 450;
/** Movement past this many px means the user is scrolling, not pressing. */
const MOVE_CANCEL_PX = 10;

type Options = {
  onLongPress: () => void;
  /** When false the handlers are inert, so a thread the viewer cannot act on costs nothing. */
  enabled?: boolean;
};

/**
 * Press-and-hold to open something, following the touch conventions of
 * `usePullToRefresh` and `useSwipeToDismiss` -- refs mirroring everything the listeners
 * read, and one `Haptics.impact` fired through a `hapticFiredRef` so it cannot double-buzz.
 *
 * Pointer events rather than touch events, so a long mouse-hold behaves the same as a
 * long tap and desktop gets the gesture for free; `onContextMenu` covers right-click.
 *
 * TWO THINGS THIS HAS TO GET RIGHT
 *
 * 1. Cancel on movement. Chat threads are scrollers, and a drag that begins on a message
 *    is how you scroll one. Without the MOVE_CANCEL_PX check every scroll opens a menu.
 *
 * 2. Swallow the click that follows. A long press still ends in `pointerup`, and the
 *    browser still dispatches `click` -- which in MessageBubble would toggle the "Seen by"
 *    card at the same moment the action menu opens. `consumeClick()` is how the caller
 *    asks "was that click the tail of a long press?", and it clears the flag as it
 *    answers so a real tap immediately afterwards still works.
 *
 * The caller is also responsible for the CSS. On iOS a long press on text raises the
 * selection magnifier and the native Copy/Look Up/Share callout, and on Android it starts
 * text selection -- both fire regardless of what JavaScript does here. The pressed element
 * needs `-webkit-touch-callout: none` and `user-select: none`. Do NOT set `touch-action`:
 * the thread scroller depends on it.
 */
export function useLongPress({ onLongPress, enabled = true }: Options) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);
  const suppressClickRef = useRef(false);
  /**
   * True while a press is being held and has not yet fired or been cancelled.
   *
   * Exposed so the pressed element can show that something is happening. A 450ms wait
   * with no feedback reads as lag; a slow squeeze over that same 450ms reads as the
   * gesture being recognised, and doubles as its progress bar.
   */
  const [isPressing, setIsPressing] = useState(false);
  const onLongPressRef = useRef(onLongPress);
  onLongPressRef.current = onLongPress;

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsPressing(false);
  }, []);

  // A press interrupted by unmount (the thread switching, say) must not fire into a
  // component that is no longer there.
  useEffect(() => clearTimer, [clearTimer]);

  const fire = useCallback(() => {
    firedRef.current = true;
    suppressClickRef.current = true;
    // Released before the menu appears, so the squeeze springs back as the menu pops out
    // of it rather than the message staying dented underneath.
    setIsPressing(false);
    void Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
    onLongPressRef.current();
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!enabled) return;
      // Right-click arrives as pointerdown too; onContextMenu already handles it.
      if (event.button != null && event.button !== 0) return;
      firedRef.current = false;
      startRef.current = { x: event.clientX, y: event.clientY };
      clearTimer();
      setIsPressing(true);
      timerRef.current = setTimeout(fire, LONG_PRESS_MS);
    },
    [enabled, clearTimer, fire],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const start = startRef.current;
      if (!start || timerRef.current == null) return;
      const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
      if (moved > MOVE_CANCEL_PX) clearTimer();
    },
    [clearTimer],
  );

  const onPointerUp = useCallback(() => {
    clearTimer();
    startRef.current = null;
  }, [clearTimer]);

  const onContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled) return;
      // Desktop parity. preventDefault so the browser's own menu does not cover ours.
      event.preventDefault();
      clearTimer();
      fire();
    },
    [enabled, clearTimer, fire],
  );

  /** True when the click now being handled is the tail of a long press. Clears as it answers. */
  const consumeClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return {
    isPressing,
    consumeClick,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onPointerLeave: onPointerUp,
      onContextMenu,
    },
  };
}
